'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, AlertCircle, Loader2, Eye, EyeOff, Lock, Mail } from 'lucide-react';

export default function AdminLoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    const msg = searchParams.get('message');
    if (msg === 'signed_out') setSignedOut(true);

    const errParam = searchParams.get('error');
    if (errParam === 'unauthorized') {
      setError('Your account is not authorized for TicketFlow Platform Administration.');
    } else if (errParam === 'auth_failed') {
      setError('Authentication failed. Please try again.');
    }

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Use service-role-backed API to check platform user status
          const res = await fetch('/api/admin/session-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: session.user.email }),
          });

          if (res.ok) {
            const data = await res.json();
            if (data.platformUser) {
              router.replace('/admin/dashboard');
              return;
            }
          }
        }
      } catch {}
      setChecking(false);
    };
    checkSession();
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter your email address and password.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Call the server-side login API which uses service role to bypass RLS
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Authentication failed. Please try again.');
        setLoading(false);
        return;
      }

      // Set the session on the client using the tokens returned from the server
      if (data.session?.access_token && data.session?.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        if (sessionError) {
          setError('Failed to establish session. Please try again.');
          setLoading(false);
          return;
        }
      }

      const platformUser = data.platformUser;

      router.replace('/admin/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0D14] flex flex-col lg:flex-row overflow-hidden">
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/4 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-orange-600/3 rounded-full blur-3xl" />
      </div>

      {/* LEFT SIDE — Brand panel (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] flex-col items-center justify-center px-16 relative z-10">
        <div className="max-w-md w-full">
          <div className="mb-10">
            <AppLogo size={200} />
          </div>
          <div className="mb-8">
            <h1 className="text-white text-4xl xl:text-5xl font-bold tracking-tight leading-tight mb-4">
              Manage the flow.
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Vendors. Tickets. Messaging. Payments.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 mb-12">
            {['Vendor Management', 'Real-time Tickets', 'SMS Delivery', 'Subscription Billing'].map((f) => (
              <span key={f} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-gray-400 text-xs font-medium">
                {f}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-xs">Powered by</span>
            <span className="text-gray-500 text-xs font-semibold tracking-wide">Clouds Envy</span>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE — Login form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-12 relative z-10">
        {/* Mobile logo */}
        <div className="lg:hidden mb-8 flex flex-col items-center">
          <AppLogo size={140} />
          <p className="text-gray-500 text-sm mt-3">Platform Administration</p>
        </div>

        <div className="w-full max-w-md">
          {signedOut && (
            <div className="mb-5 flex items-start gap-3 bg-green-500/10 border border-green-500/20 rounded-xl p-4">
              <Shield className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-green-400 text-sm">You have been securely signed out.</p>
            </div>
          )}

          <div className="bg-[#111827] border border-white/10 rounded-2xl p-8 shadow-2xl shadow-black/50">
            <div className="mb-7">
              <h2 className="text-white text-2xl font-bold tracking-tight">Welcome Back</h2>
              <p className="text-gray-500 text-sm mt-1.5">Sign in to manage the TicketFlow platform.</p>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@cloudsenvy.com"
                    autoComplete="email"
                    className="w-full bg-[#0D1117] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 transition-all"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    autoComplete="current-password"
                    className="w-full bg-[#0D1117] border border-white/10 rounded-xl pl-10 pr-11 py-3 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/30 transition-all"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex justify-end mt-2">
                  <a
                    href="/admin/forgot-password"
                    className="text-gray-500 text-xs hover:text-orange-400 transition-colors"
                  >
                    Forgot password?
                  </a>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-gray-600 text-xs mt-6">
            TicketFlow Platform Administration — Powered by Clouds Envy
          </p>
        </div>
      </div>
    </div>
  );
}
